import React, { useEffect } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import OutsideListener from 'app/components/OutsideListener'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import {
  FormControl,
  FormControlLabel,
  Paper,
  IconButton,
} from '@material-ui/core'

import CheckBox from 'app/componentV4/CheckBox'

import { sortGroupByType } from 'commons/groupLogic'

import useStyle from './styles'
import useGetGroups from './useGetGroups'
import { getHomelessTagIds, ifSelectSpecialToEmpty } from './helpers'

const GroupSelect = (props) => {
  const { selectElemText, selectedTags, onTagsChange, disabled } = props

  const classes = useStyle()
  const { formatMessage } = useIntl()

  let specialTypeId = {}

  const generateText = (selectedTags, groups = state.groups) => {
    let text
    const allTagNames = []
    groups.forEach((group) => {
      group.tags &&
        group.tags.forEach((tag) => {
          selectedTags.forEach((tagId) => {
            if (tag.id === tagId) {
              allTagNames.push(tag.name)
            }
          })
        })
    })

    text = allTagNames.splice(0, 2).join(', ')

    if (allTagNames.length) {
      text +=
        ' ' +
        formatMessage({ id: 'group.select.andMore' }) +
        ' ' +
        allTagNames.length +
        ' items'
    }

    if (!text) {
      text = selectElemText || formatMessage({ id: 'group.select.selectGroup' })
    }

    return text
  }
  const { state, setState } = useGetGroups({
    selectElemText,
    selectedTags,
    generateText,
  })

  useEffect(() => {
    if (!selectedTags.length && state.selectedTags.length) {
      setState((prevState) => ({
        ...prevState,
        selectedTags: [],
        text: formatMessage({ id: 'group.select.selectGroup' }),
      }))
    }
  }, [selectedTags, state.selectedTags])

  const isGroupChecked = (id) => {
    const currentGroup = state.groups.find((group) => group.id === id)
    let isChecked = true
    currentGroup.tags.forEach((tag) => {
      isChecked = isChecked && state.selectedTags.indexOf(tag.id) !== -1
    })
    return isChecked
  }

  const handleGroupCheck = (id) => {
    const tagIds = state.groups
      .find((group) => group.id === id)
      .tags.map((tag) => tag.id)

    if (isGroupChecked(id)) {
      let ids = state.selectedTags.filter((_id) => tagIds.indexOf(_id) === -1)

      setState({
        ...state,
        selectedTags: ids,
        text: generateText(
          state.selectedTags.filter((_id) => tagIds.indexOf(_id) === -1),
        ),
      })

      onTagsChange(ids)
    } else {
      let ids = []

      if (specialTypeId === id) {
        ids = [...new Set([...tagIds])]
      } else {
        const selectedTags = ifSelectSpecialToEmpty(
          getHomelessTagIds(specialTypeId, state),
          state,
        )
        ids = [...new Set([...selectedTags, ...tagIds])]
      }

      let generatedText = generateText(ids)

      setState({
        ...state,
        selectedTags: ids,
        text: generatedText,
      })
      onTagsChange(ids)
    }
  }

  const handleChange = (id) => {
    let newSelectedTags
    if (state.selectedTags.indexOf(id) !== -1) {
      newSelectedTags = state.selectedTags.filter((_id) => _id !== id)
      setState({
        ...state,
        text: generateText(newSelectedTags),
        selectedTags: newSelectedTags,
      })
    } else {
      const selectedTags = ifSelectSpecialToEmpty(
        getHomelessTagIds(specialTypeId, state),
        state,
      )

      newSelectedTags = [...selectedTags, id]
      let labelNames = generateText(newSelectedTags)

      setState({
        ...state,
        text: labelNames,
        selectedTags: newSelectedTags,
      })
    }
    onTagsChange(newSelectedTags)
  }

  const setPaperState = (status) => {
    if (disabled) return
    setState({
      ...state,
      isOpenPaper: status,
    })
  }

  return (
    <OutsideListener
      onClickOutside={() => setPaperState(false)}
      className={'w-full'}
    >
      <FormControl variant="outlined" className={classes.inputSelectForm}>
        <div className="flex items-center justify-between">
          <div
            className={clsx('selectTagText truncate', classes.tagsSelect, {
              [classes.placeholder]: !selectedTags.length,
            })}
            onClick={() => setPaperState(!state.isOpenPaper)}
          >
            {state.text}
          </div>

          <IconButton
            aria-label="close"
            className={'p-1 mr-3'}
            onClick={(e) => {
              e.stopPropagation()
              setPaperState(!state.isOpenPaper)
            }}
          >
            {state.isOpenPaper ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconButton>
        </div>

        {state.isOpenPaper && (
          <Paper elevation={1} className={classes.tagsSelectValues}>
            <div className={clsx('mx-16', classes.tagsSelectListContainer)}>
              {state.groups &&
                sortGroupByType(state.groups).map((group) => (
                  <div key={group.id}>
                    <FormControlLabel
                      control={
                        <CheckBox
                          checked={isGroupChecked(group.id)}
                          onChange={() => handleGroupCheck(group.id)}
                          value="checkedB"
                          color="primary"
                          inputProps={{
                            'data-testid': `parent-${group.id}`,
                          }}
                        />
                      }
                      label={group.name}
                    />
                    <div className="flex flex-col ltr:ml-16 rtl:mr-16">
                      {group.type !== 0
                        ? ((specialTypeId = group.id), '')
                        : group.tags &&
                          group.tags.map((tag) => (
                            <FormControlLabel
                              control={
                                <CheckBox
                                  checked={
                                    state.selectedTags.indexOf(tag.id) !== -1
                                  }
                                  onChange={() => handleChange(tag.id)}
                                  value="checkedB"
                                  color="primary"
                                  inputProps={{
                                    'data-testid': `tag-${tag.id}`,
                                  }}
                                />
                              }
                              label={tag.name}
                              key={tag.id}
                            />
                          ))}
                    </div>
                  </div>
                ))}
            </div>
          </Paper>
        )}
      </FormControl>
    </OutsideListener>
  )
}

GroupSelect.propTypes = {
  selectElemText: PropTypes.string,
  selectedTags: PropTypes.array,
  onTagsChange: PropTypes.func,
  disabled: PropTypes.bool,
}

export default GroupSelect
